import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GoogleLogo, Envelope, LockKey, Eye, EyeSlash, ArrowLeft, Phone } from "@phosphor-icons/react";
import CountrySelect from "./CountrySelect";
import { DEFAULT_COUNTRY } from "@/lib/countries";

/**
 * Shared auth form used by AuthDialog + /login + /signup pages.
 * mode: "signin" | "signup" | "reset"
 */
export default function AuthForm({ mode = "signin", onModeChange, onSuccess, compact = false, header = null }) {
  const {
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    sendPasswordReset,
  } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        await signInWithEmail(email, password);
        toast.success("Welcome back");
        onSuccess?.();
      } else if (mode === "signup") {
        const digits = phone.replace(/\D/g, "");
        const fullPhone = digits ? `${country.dial}${digits}` : "";
        await signUpWithEmail(email, password, {
          full_name: fullName,
          phone: fullPhone,
          phone_country: country.code,
        });
        toast.success("Account created", { description: "Check your inbox to verify your email." });
        onSuccess?.();
      } else if (mode === "reset") {
        await sendPasswordReset(email);
        toast.success("Reset link sent", { description: "Check your inbox for instructions." });
        onModeChange?.("signin");
      }
    } catch (err) {
      toast.error(err?.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      toast.error(err?.message || "Google sign-in failed");
      setBusy(false);
    }
  };

  const title =
    mode === "signup" ? "Create your account" : mode === "reset" ? "Reset password" : "Welcome back";
  const subtitle =
    mode === "signup"
      ? "Save your cart, track pre-orders, and manage research history."
      : mode === "reset"
        ? "We'll email you a secure link to set a new password."
        : "Sign in to sync your cart and access your account.";

  return (
    <div className={compact ? "" : "w-full max-w-md"}>
      {header ? (
        header
      ) : (
        <div className="text-center space-y-1.5">
          {mode === "reset" && (
            <button
              type="button"
              onClick={() => onModeChange?.("signin")}
              className="inline-flex items-center gap-1 text-[12px] text-cool-grey hover:text-cyan-brand transition-colors mb-2"
            >
              <ArrowLeft size={12} weight="bold" /> Back to sign in
            </button>
          )}
          <h2
            data-testid={`auth-title-${mode}`}
            className="font-display text-2xl md:text-3xl font-bold text-navy-deep tracking-tight"
          >
            {title}
          </h2>
          <p className="text-sm text-cool-grey">{subtitle}</p>
        </div>
      )}

      {mode !== "reset" && (
        <>
          <Button
            type="button"
            onClick={handleGoogle}
            disabled={busy}
            data-testid="auth-google"
            className="mt-6 w-full h-11 rounded-full bg-white text-navy-deep border border-[#8A97A8]/30 hover:border-cyan-brand hover:bg-white font-medium text-[14px] gap-2 shadow-sm"
          >
            <GoogleLogo size={17} weight="bold" />
            Continue with Google
          </Button>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#8A97A8]/25" />
            <span className="text-[11px] uppercase tracking-[0.14em] text-cool-grey font-mono-data">or</span>
            <div className="flex-1 h-px bg-[#8A97A8]/25" />
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} className="mt-5 space-y-4" data-testid={`auth-form-${mode}`}>
        {mode === "signup" && (
          <div>
            <Label htmlFor="fullName" className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">
              Full name
            </Label>
            <Input
              id="fullName"
              data-testid="auth-fullname"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoComplete="name"
              className="mt-1.5 h-11 rounded-xl bg-white border-[#8A97A8]/30"
              placeholder="Dr. Jane Doe"
            />
          </div>
        )}

        <div>
          <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">
            Email
          </Label>
          <div className="mt-1.5 relative">
            <Envelope
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-cool-grey"
              weight="regular"
            />
            <Input
              id="email"
              data-testid="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="h-11 pl-9 rounded-xl bg-white border-[#8A97A8]/30"
              placeholder="you@lab.com"
            />
          </div>
        </div>

        {mode === "signup" && (
          <div>
            <Label htmlFor="phone" className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">
              Phone number
            </Label>
            <div className="mt-1.5 flex">
              <CountrySelect
                value={country.code}
                onChange={(c) => setCountry(c)}
                testId="auth-country"
              />
              <div className="relative flex-1">
                <Phone
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-cool-grey"
                  weight="regular"
                />
                <Input
                  id="phone"
                  data-testid="auth-phone"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d\s\-()]/g, ""))}
                  required
                  autoComplete="tel-national"
                  className="h-11 pl-9 rounded-r-xl rounded-l-none border-l-0 border-[#8A97A8]/30 bg-white"
                  placeholder="555 000 0000"
                />
              </div>
            </div>
            <p className="mt-1 text-[10.5px] text-cool-grey">
              We&apos;ll only use this to notify you about your orders &amp; reservations.
            </p>
          </div>
        )}

        {mode !== "reset" && (
          <div>
            <div className="flex justify-between items-baseline">
              <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">
                Password
              </Label>
              {mode === "signin" && (
                <button
                  type="button"
                  onClick={() => onModeChange?.("reset")}
                  data-testid="auth-forgot"
                  className="text-[11px] text-cool-grey hover:text-cyan-brand transition-colors"
                >
                  Forgot?
                </button>
              )}
            </div>
            <div className="mt-1.5 relative">
              <LockKey
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-cool-grey"
                weight="regular"
              />
              <Input
                id="password"
                data-testid="auth-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                className="h-11 pl-9 pr-10 rounded-xl bg-white border-[#8A97A8]/30"
                placeholder={mode === "signup" ? "At least 6 characters" : "••••••••"}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cool-grey hover:text-navy-deep"
                aria-label={showPw ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid={`auth-submit-${mode}`}
          className="w-full h-11 rounded-full bg-cta-gradient hover:brightness-110 text-white font-semibold shadow-[0_8px_22px_-8px_rgba(1,207,198,0.55)]"
        >
          {busy
            ? "Please wait…"
            : mode === "signup"
              ? "Create account"
              : mode === "reset"
                ? "Send reset link"
                : "Sign in"}
        </Button>
      </form>

      {mode !== "reset" && (
        <div className="mt-5 text-center text-[13px] text-cool-grey">
          {mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => onModeChange?.("signin")}
                data-testid="auth-switch-signin"
                className="text-cyan-brand hover:underline font-medium"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              New to Aeonyx?{" "}
              <button
                type="button"
                onClick={() => onModeChange?.("signup")}
                data-testid="auth-switch-signup"
                className="text-cyan-brand hover:underline font-medium"
              >
                Create account
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
